import React, { useState } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { PRODUCT_STATES } from '../constants';
import MobileProductDialog from './MobileProductDialog';
import MobileProductField from './MobileProductField';
import MobileProductHint from './MobileProductHint';
import MobileProductChoice from './MobileProductChoice';
import MobileCategoryPicker from './MobileCategoryPicker';
import MobileProductFeedback from './MobileProductFeedback';
import useMobileMutation from './useMobileMutation';
import { readNumber } from './mobileProductUtils';

const ACTIONS = [
  ['state', 'Змінити статус'],
  ['discount', 'Змінити знижку'],
  ['discountCategoryName', 'Категорія знижки'],
  ['expectedArrivalDate', 'Дата надходження'],
  ['clearExpectedArrivalDate', 'Очистити дату надходження'],
  ['categoryIds', 'Додати категорії'],
  ['removeCategoryIds', 'Прибрати категорії'],
];
export const buildMobileBulkPayload = (ids, action, value) => {
  if (!ids.length) throw new Error('Оберіть товари');
  if (!ACTIONS.some(([key]) => key === action)) throw new Error('Оберіть масову дію');
  if (action === 'discount' && (!Number.isInteger(readNumber(value)) || readNumber(value) > 100))
    throw new Error('Знижка має бути цілим числом від 0 до 100');
  if (action === 'state' && !PRODUCT_STATES.some(state => state.value && state.value === value))
    throw new Error('Оберіть статус');
  if (action === 'expectedArrivalDate' && !value) throw new Error('Вкажіть дату надходження');
  if (['categoryIds', 'removeCategoryIds'].includes(action) && !value.length)
    throw new Error('Оберіть хоча б одну категорію');
  return { productIds: ids, [action]: action === 'clearExpectedArrivalDate' ? true : value };
};

export default function MobileBulkActions({
  ids,
  categories,
  discountCategories,
  resourcesError,
  onRetry,
  onSaved,
  onClose,
}) {
  const [action, setAction] = useState('state');
  const [value, setValue] = useState('ACTIVE');
  const [message, setMessage] = useState('');
  const mutation = useMobileMutation();
  const categoryAction = ['categoryIds', 'removeCategoryIds'].includes(action);
  const apply = () =>
    mutation.run(
      () => adminProductApi.bulkUpdateProducts(buildMobileBulkPayload(ids, action, value)),
      () => {
        setMessage(`Оновлено товарів: ${ids.length}`);
        onSaved();
      },
    );
  return (
    <MobileProductDialog
      title='Масові дії'
      subtitle={`Обрано товарів: ${ids.length}`}
      busy={mutation.busy}
      onClose={onClose}
      onSubmit={() => {
        if (!message) apply();
      }}
      footer={
        <>
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={onClose}
            disabled={mutation.busy}
          >
            Закрити
          </button>
          <button
            type='submit'
            className='btn btn-dark'
            disabled={mutation.busy || !!message || (categoryAction && !!resourcesError)}
          >
            {mutation.busy ? 'Оновлення…' : 'Застосувати'}
          </button>
        </>
      }
    >
      <MobileProductHint>
        Дія застосовується до всіх обраних товарів, включно з іншими сторінками.
      </MobileProductHint>
      <MobileProductFeedback
        error={mutation.error || resourcesError}
        message={message}
        onRetry={resourcesError && onRetry}
      />
      <fieldset disabled={mutation.busy}>
        <MobileProductField label='Що змінити'>
          <select
            className='form-control'
            value={action}
            onChange={event => {
              const next = event.target.value;
              setAction(next);
              setValue(
                ['categoryIds', 'removeCategoryIds'].includes(next)
                  ? []
                  : next === 'state'
                  ? 'ACTIVE'
                  : '',
              );
              setMessage('');
              mutation.setError('');
            }}
          >
            {ACTIONS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </MobileProductField>
        {action === 'state' && (
          <MobileProductChoice
            label='Новий статус'
            options={PRODUCT_STATES.filter(state => state.value)}
            value={value}
            onChange={next => {
              setValue(next);
              setMessage('');
            }}
          />
        )}
        {action === 'discount' && (
          <MobileProductField
            label='Знижка, %'
            type='number'
            min='0'
            max='100'
            inputMode='numeric'
            value={value}
            onChange={next => {
              setValue(next);
              setMessage('');
            }}
          />
        )}
        {action === 'discountCategoryName' && (
          <MobileProductChoice
            label='Категорія знижки'
            options={[
              { value: '', label: 'Без групи знижок' },
              ...discountCategories.map(category => ({
                value: category.name,
                label: category.name,
              })),
            ]}
            value={value}
            onChange={next => {
              setValue(next);
              setMessage('');
            }}
          />
        )}
        {action === 'expectedArrivalDate' && (
          <MobileProductField
            label='Дата надходження'
            type='date'
            value={value}
            onChange={next => {
              setValue(next);
              setMessage('');
            }}
          />
        )}
        {action === 'clearExpectedArrivalDate' && (
          <p>Дату очікуваного надходження буде очищено для обраних товарів.</p>
        )}
        {categoryAction && (
          <MobileCategoryPicker
            categories={categories}
            value={value}
            onChange={next => {
              setValue(next);
              setMessage('');
            }}
          />
        )}
      </fieldset>
    </MobileProductDialog>
  );
}
